import { useEffect } from 'react';

/**
 * Per-page SEO for a client-rendered app.
 *
 * Google renders JavaScript, so tags written into <head> after mount are what it
 * indexes — but only if every page writes a complete, page-specific set. A page
 * that writes nothing would otherwise inherit the previous page's title and
 * canonical on a client-side navigation, so the hook resets to the site defaults
 * when the page unmounts.
 *
 * `index.html` carries the same defaults statically, for crawlers and link
 * unfurlers (WhatsApp, Slack, X) that never run the bundle. Keep them in step.
 */

export const SITE_NAME = 'BookYourZone';
export const SITE_URL = 'https://bookyourzone.com';
export const DEFAULT_TITLE = 'BookYourZone – Book Sports Venues, Turfs & Courts Near You';
export const DEFAULT_DESCRIPTION =
  'Book box cricket, football turfs, badminton, pickleball, tennis and more sports venues near you. Compare prices, check live slot availability and book instantly on BookYourZone.';
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/* QA, local and tunnel hosts must never compete with production in search. */
const isIndexableHost = () => {
  const host = window.location.hostname;
  return !(
    host === 'localhost' ||
    host.startsWith('qa') ||
    host.includes('-qa') ||
    host.endsWith('.ngrok-free.app')
  );
};

export interface SeoOptions {
  title?: string;
  description?: string;
  /** Path for the canonical URL; defaults to the current path without query/hash. */
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'place';
  /** Keep the page out of the index (private, transient or error pages). */
  noindex?: boolean;
  /** One or more schema.org objects, written as a single JSON-LD script. */
  jsonLd?: object | object[];
}

const JSON_LD_ID = 'byz-page-jsonld';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd(data?: object | object[]) {
  document.getElementById(JSON_LD_ID)?.remove();
  if (!data) return;
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = JSON_LD_ID;
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
}

export function applySeo(options: SeoOptions = {}) {
  const title = options.title ? `${options.title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const description = (options.description || DEFAULT_DESCRIPTION).slice(0, 300);
  const origin = window.location.origin;
  const url = `${origin}${options.path ?? window.location.pathname}`;
  const image = options.image || DEFAULT_IMAGE;
  const robots =
    options.noindex || !isIndexableHost()
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1';

  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', robots);
  setCanonical(url);

  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', image);
  setMeta('property', 'og:type', options.type === 'article' ? 'article' : 'website');

  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);

  setJsonLd(options.jsonLd);
}

/**
 * Pass a stable `deps` list when the options are built from async data (e.g. a
 * venue that loads after mount) so the tags update once it arrives.
 */
export function useSeo(options: SeoOptions, deps: unknown[] = []) {
  useEffect(() => {
    applySeo(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => () => applySeo(), []);
}

export const breadcrumbJsonLd = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${window.location.origin}${item.path}`,
  })),
});
